import React, { useState } from "react";
import { Languages, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { translateText, detectLang, LANG_FLAG } from "@/lib/translate";
import { useAuth } from "@/lib/auth";

/**
 * Inline translate button + toggleable translated text.
 * Usage:
 *   <TranslateInline text={row.china_comments} />
 *
 * - Auto-detects source; skips button if source == target user preference.
 * - Click toggles between original and translated. Translated text cached.
 */
export function TranslateInline({ text, target: forceTarget, className = "", testId }) {
  const { user } = useAuth();
  const target = (forceTarget || user?.preferred_language || "en").toLowerCase();
  const clean = (text || "").trim();
  const [translated, setTranslated] = useState(null);
  const [showing, setShowing] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!clean) return null;
  const src = detectLang(clean);
  if (src === target) {
    return <span className={className}>{clean}</span>;
  }

  const doTranslate = async () => {
    if (translated) {
      setShowing((s) => !s);
      return;
    }
    setBusy(true);
    try {
      const r = await translateText(clean, target);
      setTranslated(r.translated_text || clean);
      setShowing(true);
    } catch (_e) {
      toast.error("Translation failed");
    }
    setBusy(false);
  };

  return (
    <span className={`inline-flex flex-col gap-0.5 ${className}`}>
      <span>{showing && translated ? translated : clean}</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); doTranslate(); }}
        data-testid={testId || "translate-inline-btn"}
        disabled={busy}
        className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 hover:text-blue-800 hover:underline w-fit disabled:opacity-50"
        title={showing ? `Show original (${src.toUpperCase()})` : `Translate to ${LANG_FLAG[target]}`}
      >
        {busy ? <RefreshCw size={9} className="animate-spin" /> : <Languages size={9} />}
        {showing ? `Show ${LANG_FLAG[src]}` : `Translate → ${LANG_FLAG[target]}`}
      </button>
    </span>
  );
}
